import React from 'react';
import ManagedDisclaimer from '../../../components/ManagedDisclaimer';
import { DISCLAIMER_KEY } from '../utils/disclaimer';
import { CUSTOM_FUND_NOTE, CURRENT_WEIGHTS_NOTE } from '../utils/allocationMath';

const fmtPct = (v) => `${v.toFixed(2)}%`;
const fmtDollars = (v) => `$${Math.round(v).toLocaleString()}`;
const fmtSignedPct = (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
const fmtSignedDollars = (v) => {
  const abs = `$${Math.abs(Math.round(v)).toLocaleString()}`;
  return v > 0 ? `+${abs}` : v < 0 ? `−${abs}` : abs;
};
const changeClass = (v) => (v > 0 ? 'pos' : v < 0 ? 'neg' : '');

// Output table: retained cash pinned on top, asset-class header rows with
// subtotals, fund rows (name / APIR / % / $), total footing to 100% / total $,
// growth/defensive summary, then the compliance disclaimer. In comparison mode
// every row carries Current % / $, Target % / $ and the signed Change.
const FigureCells = ({ f, comparison }) => (
  <>
    {comparison && (
      <>
        <td className="num-col">{fmtPct(f.curPct)}</td>
        <td className="num-col">{fmtDollars(f.curDollars)}</td>
      </>
    )}
    <td className="num-col">{fmtPct(f.pct)}</td>
    <td className="num-col">{fmtDollars(f.dollars)}</td>
    {comparison && (
      <>
        <td className={`num-col alloc-change ${changeClass(f.changePct)}`}>{fmtSignedPct(f.changePct)}</td>
        <td className={`num-col alloc-change ${changeClass(f.changeDollars)}`}>{fmtSignedDollars(f.changeDollars)}</td>
      </>
    )}
  </>
);

const ExposureBlock = ({ side, e }) => (
  <div className="alloc-exposure-block">
    {side && <div className="alloc-exposure-side">{side}</div>}
    <div className="alloc-exposure-bar" aria-hidden="true">
      <div className="alloc-exposure-growth" style={{ width: `${Math.max(0, Math.min(100, e.growthPct))}%` }} />
    </div>
    <div className="alloc-exposure-labels">
      <span className="alloc-exposure-item growth">
        Growth assets: <strong>{fmtPct(e.growthPct)}</strong> ({fmtDollars(e.growthDollars)})
      </span>
      <span className="alloc-exposure-item defensive">
        Defensive assets: <strong>{fmtPct(e.defensivePct)}</strong> ({fmtDollars(e.defensiveDollars)})
      </span>
    </div>
  </div>
);

const AllocationTable = ({ allocation }) => {
  const { retainedRows, sectors, totalRow, exposure, comparison, warnings } = allocation;
  const hasCustom = sectors.some((s) => s.rows.some((r) => r.isCustom));

  return (
    <div className="alloc-output alloc-print-area">
      {warnings.length > 0 && (
        <div className="alert alert-warning alloc-warnings">
          {warnings.map((w) => <div key={w}>{w}</div>)}
        </div>
      )}

      <div className="alloc-table-wrap">
        <table className={`alloc-table${comparison ? ' comparison' : ''}`}>
          <thead>
            <tr>
              <th className="name-col">Asset Class / Investment Option</th>
              <th className="code-col">ASX / APIR Code</th>
              {comparison ? (
                <>
                  <th className="num-col">Current %</th>
                  <th className="num-col">Current $</th>
                  <th className="num-col">Target %</th>
                  <th className="num-col">Target $</th>
                  <th className="num-col">Change %</th>
                  <th className="num-col">Change $</th>
                </>
              ) : (
                <>
                  <th className="num-col">%</th>
                  <th className="num-col">$</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(retainedRows || []).map((r) => (
              <tr key={r.key} className="alloc-retained-row">
                <td className="name-col">{r.name}</td>
                <td className="code-col" />
                <FigureCells f={r} comparison={comparison} />
              </tr>
            ))}
            {sectors.map((sector) => (
              <React.Fragment key={sector.label}>
                <tr className="alloc-sector-row">
                  <td className="name-col">{sector.label}</td>
                  <td className="code-col" />
                  <FigureCells
                    comparison={comparison}
                    f={{
                      pct: sector.subtotalPct, dollars: sector.subtotalDollars,
                      curPct: sector.subtotalCurPct, curDollars: sector.subtotalCurDollars,
                      changePct: sector.changePct, changeDollars: sector.changeDollars,
                    }}
                  />
                </tr>
                {sector.rows.map((r) => (
                  <tr key={r.key} className={`alloc-fund-row${r.isDirect ? ' direct' : ''}`}>
                    <td className="name-col">{r.name}{r.isCustom ? ' *' : ''}</td>
                    <td className="code-col">{r.code || '—'}</td>
                    <FigureCells f={r} comparison={comparison} />
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr className="alloc-total-row">
              <td className="name-col">Total</td>
              <td className="code-col" />
              <FigureCells f={totalRow} comparison={comparison} />
            </tr>
          </tbody>
        </table>
      </div>

      {exposure && (
        <div className="alloc-exposure">
          {comparison && exposure.current && <ExposureBlock side="Current" e={exposure.current} />}
          <ExposureBlock side={comparison ? 'Target' : null} e={exposure} />
        </div>
      )}

      {hasCustom && <div className="alloc-custom-note">{CUSTOM_FUND_NOTE}</div>}
      {comparison && <div className="alloc-custom-note">{CURRENT_WEIGHTS_NOTE}</div>}

      <ManagedDisclaimer dkey={DISCLAIMER_KEY} className="alloc-disclaimer" />
    </div>
  );
};

export default AllocationTable;
